import { ModuleTypeEnum } from '../module'
import { AccountType } from '../organization'
import { TodayWidgetDefinition } from './widget'

export const TODAY_WIDGETS: TodayWidgetDefinition[] = [
  {
    id: 'overview-stats',
    layer: 'workspace',
    label: 'Overview Stats',
    description: 'Key statistics and metrics for your business',
    moduleType: ModuleTypeEnum.ORGANIZATION,
    defaultColumn: 'stats',
    allowedColumns: ['stats'],
    columnType: 'main',
    defaultSize: 'medium',
    isActive: false,
    icon: 'BarChart3',
  },
  {
    id: 'my-tasks',
    layer: 'work',
    label: 'My Work',
    description:
      "Your assigned tasks and tickets organized by when they're due",
    moduleType: ModuleTypeEnum.ORGANIZATION,
    defaultColumn: 'tasks',
    allowedColumns: ['tasks', 'main'],
    columnType: 'main',
    defaultSize: 'medium',
    minSize: 'medium',
    isDefault: true,
    icon: 'ListTodo',
  },
  {
    id: 'quick-actions',
    layer: 'work',
    label: 'Quick Actions',
    description: 'Shortcuts to common tasks and actions',
    moduleType: ModuleTypeEnum.ORGANIZATION,
    defaultColumn: 'actions',
    allowedColumns: ['actions'],
    columnType: 'sidebar',
    defaultSize: 'small',
    isDefault: true,
    icon: 'Zap',
  },
  {
    id: 'activity',
    layer: 'workspace',
    label: 'Recent Activity',
    description: 'Recent activities and updates across your workspace',
    moduleType: ModuleTypeEnum.ORGANIZATION,
    defaultColumn: 'activity',
    allowedColumns: ['activity'],
    columnType: 'sidebar',
    defaultSize: 'small',
    isDefault: true,
    icon: 'Activity',
  },
  {
    id: 'favorites',
    layer: 'work',
    label: 'Favorites',
    description: 'Quick access to your favorite entities and projects',
    moduleType: ModuleTypeEnum.ORGANIZATION,
    defaultColumn: 'favorites',
    allowedColumns: ['favorites', 'sidebar'],
    columnType: 'sidebar',
    defaultSize: 'small',
    maxSize: 'medium',
    isDefault: true,
    icon: 'Star',
  },
  // Ops Center widgets
  {
    id: 'action-center',
    layer: 'now',
    label: 'Action Center',
    description: 'All items requiring your attention',
    moduleType: ModuleTypeEnum.ORGANIZATION,
    defaultColumn: 'main',
    allowedColumns: ['main'],
    columnType: 'main',
    defaultSize: 'medium',
    isDefault: false,
    isActive: false,
    icon: 'BellRinging',
  },
  {
    id: 'todays-schedule',
    layer: 'now',
    label: 'Up Next',
    description: "A live timeline of today's meetings and tasks",
    moduleType: ModuleTypeEnum.CALENDAR,
    defaultColumn: 'main',
    allowedColumns: ['main'],
    columnType: 'main',
    defaultSize: 'medium',
    isDefault: true,
    icon: 'CalendarCheck',
  },
  {
    id: 'time-tracker',
    layer: 'now',
    label: 'Time Tracker',
    description: "Active timer and today's logged time",
    moduleType: ModuleTypeEnum.TIME_ENTRY,
    accountTypes: [AccountType.ORGANIZATION],
    defaultColumn: 'time',
    allowedColumns: ['time', 'sidebar'],
    columnType: 'sidebar',
    defaultSize: 'small',
    isDefault: true,
    icon: 'Timer',
  },
  {
    id: 'unified-inbox',
    layer: 'now',
    label: 'Inbox',
    description: 'Approvals, overdue work, and notifications waiting on you',
    moduleType: ModuleTypeEnum.ORGANIZATION,
    defaultColumn: 'inbox',
    allowedColumns: ['inbox', 'sidebar'],
    columnType: 'sidebar',
    defaultSize: 'small',
    // Off by default since 2026-09-01: the same notifications already appear in
    // the bell and the briefing sentence, so a default Today card made it a
    // third copy of one idea. Still fully available in "Add widgets"
    // (isActive is unset, i.e. true) and untouched in existing saved layouts.
    isDefault: false,
    icon: 'Tray',
  },
  {
    id: 'quick-notes',
    layer: 'work',
    label: 'Quick Notes',
    description: 'Your quick notes, always at hand',
    moduleType: ModuleTypeEnum.NOTE,
    defaultColumn: 'notes',
    allowedColumns: ['notes', 'sidebar'],
    columnType: 'sidebar',
    defaultSize: 'small',
    isDefault: false,
    icon: 'Note',
  },
  {
    id: 'team-pulse',
    layer: 'workspace',
    label: 'Team Pulse',
    description: 'See what your team is working on',
    moduleType: ModuleTypeEnum.TEAM,
    accountTypes: [AccountType.ORGANIZATION],
    defaultColumn: 'team',
    allowedColumns: ['team', 'main'],
    columnType: 'main',
    defaultSize: 'medium',
    isDefault: false,
    icon: 'UsersThree',
  },
  // Personal widgets — only meaningful on PERSONAL accounts (members +
  // household vendors). The accountTypes gate hides them from org pickers and
  // layouts; on org accounts they never render. Default-on for personal via
  // isDefault (org accounts filter them out at render, mirroring how
  // time-tracker/team-pulse are default-on but hidden from personal).
  {
    id: 'people-quick-access',
    layer: 'workspace',
    label: 'Members',
    description: 'Quick access to the people sharing your space',
    moduleType: ModuleTypeEnum.MEMBER,
    accountTypes: [AccountType.PERSONAL],
    defaultColumn: 'sidebar',
    allowedColumns: ['sidebar', 'favorites'],
    columnType: 'sidebar',
    defaultSize: 'small',
    maxSize: 'medium',
    isDefault: true,
    icon: 'UsersThree',
  },
  {
    id: 'vendors-quick-access',
    layer: 'workspace',
    label: 'Vendors',
    description: 'Your household vendors with one-tap call and email',
    moduleType: ModuleTypeEnum.SUPPLIER,
    accountTypes: [AccountType.PERSONAL],
    defaultColumn: 'sidebar',
    allowedColumns: ['sidebar', 'favorites'],
    columnType: 'sidebar',
    defaultSize: 'small',
    maxSize: 'medium',
    isDefault: true,
    icon: 'Wrench',
    // Vendors retired for personal spaces (2026-07-17) — this was a
    // personal-only widget, so it's inactive everywhere now.
    isActive: false,
  },
  // Family daily-habit loop — PERSONAL-only widgets. Same accountTypes gate as
  // the quick-access widgets above: hidden from org pickers/layouts, default-on
  // for personal via isDefault.
  {
    // Work-account streaks, weekly goals and badges (GAMIFICATION flag).
    // Personal spaces keep the Chore Chart's family stars instead.
    id: 'engagement',
    layer: 'work',
    label: 'Streak & goals',
    description: 'Your working-day streak, weekly goals and badges',
    moduleType: ModuleTypeEnum.ORGANIZATION,
    accountTypes: [AccountType.ORGANIZATION],
    featureFlag: 'GAMIFICATION',
    // Center column: it reads as part of your work, next to My Work.
    defaultColumn: 'main',
    allowedColumns: ['main', 'sidebar'],
    columnType: 'main',
    defaultSize: 'medium',
    minSize: 'small',
    maxSize: 'large',
    isDefault: true,
    icon: 'Flame',
  },
  {
    id: 'chore-chart',
    layer: 'work',
    label: 'Chore Chart',
    description: "Each family member's stars, streaks, and chore progress",
    moduleType: ModuleTypeEnum.TASK,
    accountTypes: [AccountType.PERSONAL],
    defaultColumn: 'sidebar',
    allowedColumns: ['sidebar', 'main'],
    columnType: 'sidebar',
    defaultSize: 'medium',
    minSize: 'small',
    maxSize: 'large',
    isDefault: true,
    icon: 'Trophy',
  },
  {
    id: 'family-lists',
    layer: 'work',
    label: 'Lists',
    description: 'Your grocery, packing, and household lists at a glance',
    moduleType: ModuleTypeEnum.PROJECT,
    accountTypes: [AccountType.PERSONAL],
    defaultColumn: 'main',
    allowedColumns: ['main'],
    columnType: 'main',
    defaultSize: 'medium',
    minSize: 'small',
    maxSize: 'large',
    isDefault: true,
    icon: 'ListChecks',
  },
  {
    id: 'family-pulse',
    layer: 'work',
    label: 'Family Pulse',
    description: "See where everyone's at and set your own status",
    moduleType: ModuleTypeEnum.MEMBER,
    accountTypes: [AccountType.PERSONAL],
    defaultColumn: 'sidebar',
    allowedColumns: ['sidebar', 'main'],
    columnType: 'sidebar',
    defaultSize: 'small',
    maxSize: 'medium',
    isDefault: true,
    icon: 'Heartbeat',
  },
]
